/**
 * Nepal-jurisdiction legal text for wateriia.com.
 *
 * Served at `/np/policy/<slug>`, so a single domain can carry the policy links
 * for all three Play Store / App Store listings (India, UAE, Nepal).
 *
 * IMPORTANT — this is a copy of `website/wateriia.com.np/src/lib/policies.js`,
 * which serves the same text to visitors of the Nepali storefront. The two are
 * separate CRA apps and cannot share a module, so they must be edited together.
 * Run `node scripts/check-policy-parity.mjs` from `website/` to confirm they
 * still match.
 *
 * Section shape: { h, p?, list?, note? }
 */

/** Nepal entity and contact details, mirroring wateriia.com.np/src/lib/constants.js. */
const BRAND = {
  name: "Wateriia Nepal",
  legal: "Wateriia Enterprises",
  phone: "+977 9744433838",
  email: "info@wateriia.com.np",
  address: "Ground Floor, Trade Tower Building, NH03, Kathmandu 06530, Nepal",
};

export const POLICIES_NP = {
  "privacy-policy": {
    title: "Privacy Policy",
    subtitle:
      "What the Wateriia app and website collect, why we collect it, who we share it with, and the control you have over it.",
    storeNote:
      "This is the privacy policy referenced by the Wateriia listing on Google Play and the Apple App Store.",
    sections: [
      {
        h: "1. Who we are",
        p: `Wateriia Nepal is operated by ${BRAND.legal}, registered in Nepal with its office at ${BRAND.address}. In this policy "Wateriia", "we" and "us" mean ${BRAND.legal}. We are the data controller for the personal information described below. You can reach us at ${BRAND.email} or ${BRAND.phone}.`,
      },
      {
        h: "2. Scope",
        p: `This policy covers the Wateriia mobile application for Android and iOS, the website at wateriia.com.np, and the support channels we operate — phone, email, WhatsApp and in-app support. It does not cover third-party websites or apps you reach through a link from ours.`,
      },
      {
        h: "3. Information you give us",
        list: [
          "Account details — your name, mobile number, email address and password. Your mobile number is verified by one-time password (OTP) before an account is created.",
          "Delivery addresses — street address, landmark, city, area or ward, pin code, optional building or flat number, and any delivery instruction you save.",
          "Order information — the products, volumes and quantities you order, the delivery slot, the subscription schedule, and any note attached to an order.",
          "Payment information — the payment method you choose and the transaction reference returned by the payment provider. We do not store your full card number, CVV, UPI PIN, wallet PIN or bank password.",
          "Support content — messages, screenshots and attachments you send when you raise a support ticket, dispute an order, or use the contact form.",
          "Partner and enquiry forms — the business details you submit if you apply to partner with us.",
        ],
      },
      {
        h: "4. Information we collect automatically",
        list: [
          "Device and app data — device model, operating system version, app version, language, and a device identifier used to keep you signed in.",
          "Approximate and precise location — only when you grant the permission. We use it to check whether your address is inside a serviceable zone, to estimate delivery distance and charges, and to show the assigned rider on a map while an order is in progress. You can decline location access and type your address manually instead; the app remains usable.",
          "Push notification token — a Firebase Cloud Messaging token, so we can send order and delivery updates.",
          "Usage and diagnostic data — screens opened, features used, crashes and error logs, and server request logs that include your IP address. We use this to keep the service working and to fix faults.",
          "Cookies and similar storage — see our Cookie Policy.",
        ],
      },
      {
        h: "5. Device permissions the app asks for",
        p: `Every permission is optional, is requested in context when the feature needs it, and can be withdrawn at any time in your device settings.`,
        list: [
          "Location (while using the app) — serviceability checks, delivery distance and charges, live rider tracking.",
          "Notifications — order confirmations, dispatch and delivery alerts, subscription reminders.",
          "Photos and media — only when you attach an image to a support ticket or save a payment receipt. We do not browse your photo library.",
          "Network state — to detect whether you are online and to retry failed requests.",
        ],
      },
      {
        h: "6. Why we use your information",
        list: [
          "To create and secure your account and confirm it is really you, through OTP and session management.",
          "To accept, process, deliver and invoice your orders and subscriptions.",
          "To take payment, issue refunds, and manage returnable-jar deposits.",
          "To give you order status, delivery tracking and receipts.",
          "To answer your questions and resolve complaints, disputes and quality issues.",
          "To detect and prevent fraud, abuse and misuse of the platform.",
          "To meet our legal, tax and accounting obligations in Nepal.",
          "To improve the service — measuring which features are used, diagnosing crashes, and planning delivery coverage.",
          "To send you marketing about Wateriia offers, only where you have opted in. Every marketing message includes a way to opt out, and opting out never affects your order notifications.",
        ],
      },
      {
        h: "7. Who we share it with",
        p: `We do not sell your personal information. We share only what is necessary, with:`,
        list: [
          "Delivery riders and partner stores — your name, delivery address, phone number and order contents, so the order can be fulfilled.",
          "Payment providers — eSewa, Khalti, card processors and banks, to authorise and settle your payment and to process refunds. They handle your payment credentials under their own privacy policies; we never see them.",
          "Infrastructure and communication providers — cloud hosting, database, SMS and email gateways, and Google Firebase for push notifications and crash reporting.",
          "Mapping providers — to render maps, geocode your address and estimate routes.",
          "Analytics providers — in aggregated or pseudonymised form, to understand how the app is used.",
          "Professional advisers, auditors and insurers — where needed, and under a duty of confidentiality.",
          "Government authorities, regulators and courts — where we are legally required to disclose, or to establish or defend a legal claim.",
          "A successor entity — if the business or part of it is reorganised, merged or transferred, with notice to you.",
        ],
      },
      {
        h: "8. Where your information is stored",
        p: `Our servers and backups are hosted with reputable cloud providers. Some of our service providers — for example push notification, email, SMS and analytics providers — process data on infrastructure outside Nepal. Where that happens we require contractual confidentiality and security commitments from them, and share only the minimum data the service needs.`,
      },
      {
        h: "9. How long we keep it",
        p: `We keep personal information only as long as needed for the purpose we collected it, or as long as Nepali law requires. The exact period for each category — account data, order and payment records, support tickets, location and log data — is set out in our Data Retention Policy.`,
      },
      {
        h: "10. How we protect it",
        list: [
          "All traffic between the app, the website and our servers is encrypted in transit using HTTPS and TLS.",
          "Passwords are stored only as salted one-way hashes. Nobody at Wateriia can read your password.",
          "Sessions are token-based and can be revoked. OTPs are single-use and short-lived.",
          "Access to production data is limited to the staff who need it, is role-based, and is written to an audit trail.",
          "Payment credentials never reach our servers — they are entered directly with the payment provider.",
          "We keep our servers patched and back data up regularly.",
        ],
        note: `No system is perfectly secure. If a breach affects your personal information we will act promptly to contain it, and will notify you and any relevant authority where required.`,
      },
      {
        h: "11. Your rights and choices",
        list: [
          "Access — ask for a copy of the personal information we hold about you.",
          "Correction — update your name, phone, email and addresses in the app, or ask us to correct anything you cannot edit yourself.",
          "Deletion — ask us to delete your account and the personal data attached to it. See Delete Your Account & Data for what is removed, what we must keep, and how long it takes.",
          "Withdraw consent — turn off location or notification permissions in your device settings, or unsubscribe from marketing at any time.",
          "Object or restrict — ask us to stop a particular use of your data where you have grounds to object.",
          `Complain — write to us first at ${BRAND.email}. If you are not satisfied, you may approach the competent authority in Nepal.`,
        ],
        note: `To exercise any of these rights, email ${BRAND.email} from your registered email address, or call ${BRAND.phone}. We may ask you to verify your identity before we act, and we aim to respond within 30 days.`,
      },
      {
        h: "12. Children",
        p: `Wateriia is not directed at children. You must be at least 18 to hold an account and place an order. We do not knowingly collect personal information from anyone under 18. If you believe a child has given us personal information, contact ${BRAND.email} and we will delete it. See also our Child Safety Policy.`,
      },
      {
        h: "13. Automated decisions",
        p: `We use simple automated rules to check whether an address is serviceable, to assign riders and delivery slots, and to flag unusual ordering patterns for review. These rules do not make decisions with legal consequences for you, and a person reviews any account restriction before it is applied.`,
      },
      {
        h: "14. Changes to this policy",
        p: `We may update this policy as the service or the law changes. The version in force is always the one published here, with the "last updated" date at the top of the page. If a change materially affects how we use your information, we will notify you in the app, by email or by SMS before it takes effect.`,
      },
      {
        h: "15. Contact us",
        p: `Privacy questions, requests and complaints: email ${BRAND.email}, call ${BRAND.phone}, or write to ${BRAND.legal}, ${BRAND.address}.`,
      },
    ],
  },

  "terms-and-conditions": {
    title: "Terms & Conditions",
    subtitle:
      "The agreement between you and Wateriia Nepal covering use of the app, the website, and every order you place.",
    storeNote:
      "These terms also serve as the end-user licence agreement (EULA) for the Wateriia mobile application.",
    sections: [
      {
        h: "1. Agreement to these terms",
        p: `These Terms & Conditions are a binding agreement between you and ${BRAND.legal} ("Wateriia"). By creating an account, installing or using the Wateriia app, using wateriia.com.np, or placing an order, you accept these terms and the policies linked from them. If you do not accept them, please do not use the service.`,
      },
      {
        h: "2. Eligibility",
        p: `You must be at least 18 years old and legally able to enter into a contract. You confirm that the information you give us — name, phone number, address — is accurate, current, and yours to provide. A business account must be opened by someone authorised to bind that business.`,
      },
      {
        h: "3. Your account",
        list: [
          "One account per mobile number. Your number is verified by OTP.",
          "Keep your device, your OTP and your login credentials confidential. Activity through your account is treated as yours.",
          `Tell us immediately at ${BRAND.email} if you suspect unauthorised access, so that we can revoke your sessions.`,
          "We may suspend or close an account used for fraud, repeated non-payment, abuse of delivery staff, or breach of these terms.",
        ],
      },
      {
        h: "4. Licence to use the app",
        p: `We grant you a personal, limited, non-exclusive, non-transferable, revocable licence to install and use the Wateriia app on devices you own or control, solely to use our services. You may not copy, modify, decompile, reverse engineer, rent, resell or sublicense the app, remove any proprietary notice, use it to build a competing service, or access it by automated means. The licence ends when you stop using the app or when we terminate it. Your use of the app must also comply with the rules of the app store you downloaded it from.`,
      },
      {
        h: "5. Products, prices and availability",
        list: [
          "All prices are in Nepali Rupees (NPR) and include applicable taxes unless the checkout screen says otherwise.",
          "Delivery charges and any deposit on returnable jars are shown separately at checkout, before you pay.",
          "Prices, offers, product ranges and service areas can change at any time without notice. The price shown when you confirm the order is the price that applies to it.",
          "Product images are illustrative. Packaging, label design and bottle appearance may vary between batches.",
          "If a listed price is obviously wrong because of a technical or human error, we may cancel the affected order and refund you in full rather than supply at that price.",
        ],
      },
      {
        h: "6. How an order is formed",
        p: `Placing an order is an offer to buy. We may accept or decline it — for example if stock has run out, your address is outside a serviceable zone, the order appears fraudulent, or a pricing error has occurred. A contract forms only when we confirm that the order is accepted and dispatched. Until then no contract exists, and any amount you have paid is refundable in full.`,
      },
      {
        h: "7. Payment",
        list: [
          "You can pay by the methods shown at checkout — cash on delivery, digital wallet (eSewa, Khalti), card, or bank transfer, subject to availability.",
          "Online payments are processed by licensed third-party providers. We receive only a confirmation and a reference; we never receive your card number, wallet PIN or banking password.",
          "If a payment is authorised but the order fails, the amount is refunded to the original method as described in the Return & Refund Policy.",
          "For cash on delivery, please have the exact amount ready. Riders carry limited change.",
          "Unpaid or disputed balances may lead to suspension of further deliveries until they are settled.",
        ],
      },
      {
        h: "8. Subscriptions and scheduled deliveries",
        list: [
          "A subscription repeats a delivery on the schedule you choose. You can pause, skip, reschedule or cancel it in the app.",
          "Changes and cancellations take effect for deliveries not yet dispatched. A delivery already out for dispatch will still be completed and charged.",
          "We will tell you in advance if the price of a subscription changes, and you may cancel before the new price applies.",
          "We may pause a subscription if payment fails or the address becomes unserviceable, and will contact you when we do.",
        ],
      },
      {
        h: "9. Delivery",
        p: `Delivery is governed by our Shipping & Delivery Policy. Estimated times are indicative, not guaranteed, and may be affected by traffic, weather, road closures, bandhs, festivals or other events outside our control. Risk in the goods passes to you on delivery to the address or the person you nominated.`,
      },
      {
        h: "10. Returnable jars and deposits",
        list: [
          "20-litre jars are supplied on a returnable basis. Any deposit is shown at checkout and is refundable when undamaged, usable jars are returned.",
          "Jars remain our property. Please keep them clean, capped, and away from direct sunlight, fuel, chemicals and sewage.",
          "Jars that are cracked, burnt, cut, contaminated, or used to store anything other than drinking water cannot be accepted, and the deposit for those units is forfeited.",
          "If you close your account, return any outstanding jars to claim the deposit.",
        ],
      },
      {
        h: "11. Returns, cancellations and refunds",
        p: `Your cancellation, replacement and refund rights are set out in the Return & Refund Policy, which forms part of these terms.`,
      },
      {
        h: "12. Product quality and safe use",
        list: [
          "Our water is intended for drinking and ordinary household or commercial use. We supply it in line with the applicable Nepali standards for packaged drinking water.",
          "Check the seal before accepting a delivery. Refuse any bottle whose seal is broken, whose contents look or smell unusual, or that is past its best-before date.",
          "Store bottles in a cool, shaded, clean place, and consume within the period marked on the label after opening.",
          "We make no medical, therapeutic or nutritional claim. Nothing in the app is health advice; ask a qualified professional for that.",
        ],
      },
      {
        h: "13. Acceptable use",
        p: `You agree not to:`,
        list: [
          "Place false, fraudulent or speculative orders, or repeatedly refuse deliveries you ordered.",
          "Use another person's payment method, phone number or account without their authority.",
          "Abuse, threaten, harass or discriminate against our staff, our riders or our partner stores.",
          "Attempt to gain unauthorised access to our systems, probe or scan them, bypass rate limits, or interfere with their operation.",
          "Scrape, harvest or reuse our content, prices or data by automated means.",
          "Upload anything unlawful, infringing, malicious or obscene through support tickets or forms.",
          "Resell our products as your own, or misrepresent an association with Wateriia.",
        ],
      },
      {
        h: "14. Intellectual property",
        p: `The Wateriia name, logo, app, website, designs, text, photographs, graphics and software are owned by ${BRAND.legal} or our licensors and are protected by law. You may not use them without our prior written permission. Feedback you send us may be used to improve the service without obligation to you, but we will not publish your name without your consent.`,
      },
      {
        h: "15. Third-party services",
        p: `The app and the website use third-party services — payment gateways, maps, push notifications, analytics, social links. Their content and practices are theirs, not ours, and they have their own terms and privacy policies. We are not responsible for third-party services, and reaching one through our platform is not an endorsement of it.`,
      },
      {
        h: "16. Availability of the service",
        p: `We aim to keep the app and the website available, but we do not guarantee uninterrupted or error-free operation. We may suspend or withdraw all or part of the service for maintenance, security or business reasons, and may change or discontinue features. Where a change is significant and notice is possible, we will give it.`,
      },
      {
        h: "17. Disclaimers",
        p: `Except as expressly stated in these terms and as required by the Consumer Protection Act, 2075 and other applicable Nepali law, the service and the app are provided "as is" and "as available", without warranties of any kind. Nothing in these terms excludes or limits any right you have that cannot lawfully be excluded.`,
      },
      {
        h: "18. Limitation of liability",
        p: `To the maximum extent permitted by applicable Nepali law, Wateriia is not liable for indirect, incidental, special, punitive or consequential loss, or for loss of profit, business, goodwill or data, arising from your use of the platform or the products. Where we are found liable, our total liability for any claim relating to an order is limited to the amount you paid for that order. Nothing here limits liability for death or personal injury caused by our negligence, for fraud, or for anything else that cannot lawfully be limited.`,
      },
      {
        h: "19. Indemnity",
        p: `You agree to indemnify us against loss, liability and reasonable costs arising from your breach of these terms, your misuse of the platform, or your unlawful acts in connection with the service.`,
      },
      {
        h: "20. Force majeure",
        p: `Neither party is liable for failure or delay caused by events beyond reasonable control — including natural disaster, earthquake, flood, epidemic, fire, war, civil unrest, bandh, strike, government action, power failure, fuel shortage, or failure of telecommunications or internet infrastructure.`,
      },
      {
        h: "21. Suspension and termination",
        p: `You may stop using the service and close your account at any time — see Delete Your Account & Data. We may suspend or terminate your access, with notice where practicable, if you breach these terms or if we reasonably suspect fraud or a risk to our staff, customers or systems. Termination does not affect rights and obligations already accrued, including payment for goods delivered and the return of our jars.`,
      },
      {
        h: "22. Changes to these terms",
        p: `We may update these terms. The current version is always published here with its "last updated" date. For material changes we will give notice in the app, by email or by SMS. Continuing to use the service after a change takes effect means you accept the updated terms.`,
      },
      {
        h: "23. Governing law and disputes",
        p: `These terms are governed by the laws of Nepal. Please contact ${BRAND.email} first — most issues are resolved quickly. If a dispute cannot be settled amicably it is subject to the exclusive jurisdiction of the competent courts of Kathmandu, without prejudice to any statutory consumer remedy available to you.`,
      },
      {
        h: "24. General",
        p: `If any provision of these terms is held unenforceable, the rest remains in force. Our failure to enforce a provision is not a waiver of it. You may not transfer your rights under these terms without our consent; we may transfer ours as part of a business reorganisation. These terms, together with the policies they reference, are the entire agreement between us on their subject matter.`,
      },
      {
        h: "25. Contact",
        p: `${BRAND.legal} — ${BRAND.address}. Email ${BRAND.email}, phone ${BRAND.phone}.`,
      },
    ],
  },

  "return-refund-policy": {
    title: "Return, Refund & Cancellation Policy",
    subtitle:
      "Cancelling an order, returning a product, claiming a replacement, and how and when your money comes back.",
    storeNote:
      "This is the return and refund policy referenced by the Wateriia app store listings.",
    sections: [
      {
        h: "1. Summary",
        p: `Cancel free of charge any time before your order is dispatched. If what arrives is damaged, leaking, unsealed, wrong or short, tell us within 24 hours and we will replace it or refund you. Approved refunds reach the original payment method within 7 to 14 business days. Sealed drinking water that has been opened cannot be returned for hygiene and food-safety reasons, unless there is a genuine quality defect.`,
      },
      {
        h: "2. Cancelling an order",
        list: [
          `Before dispatch — cancel in the app under My Orders, or call ${BRAND.phone}. No charge, and any amount already paid is refunded in full.`,
          "After dispatch but before delivery — call us immediately and we will try to stop the rider. If the rider has already reached your address, a delivery charge may be retained.",
          "At the door — you may refuse the delivery. If you refuse without a quality reason, a delivery charge may be applied after repeated refusals.",
          "Subscriptions — pause, skip or cancel in the app. Changes apply to deliveries not yet dispatched.",
        ],
      },
      {
        h: "3. Orders we cancel",
        p: `We may cancel an order if stock is unavailable, your address is outside a serviceable zone, we cannot reach you to complete delivery, a pricing or listing error occurred, or the order appears fraudulent. When we cancel, you are refunded in full including any delivery charge, and we will tell you why.`,
      },
      {
        h: "4. What you can return",
        list: [
          "Damaged on arrival — a cracked bottle, a leaking jar, a torn case, or a broken or tampered seal.",
          "Wrong item — a different product, volume or quantity from what you ordered.",
          "Short delivery — fewer units than you paid for.",
          "Quality concern — water with an unusual colour, smell or taste, or with visible particles.",
          "Expired stock — a product delivered at or past its best-before date.",
        ],
      },
      {
        h: "5. What cannot be returned",
        list: [
          "Sealed drinking water that has been opened or partly consumed, unless a genuine quality defect is established. This is a food-safety requirement, not a commercial one.",
          "Products damaged after delivery by mishandling, improper storage, or exposure to sunlight, heat or chemicals.",
          "Returnable jars that are cracked, burnt, cut, contaminated, or were used to store anything other than drinking water.",
          "Items bought under a clearance or explicitly non-returnable offer, other than for a defect.",
          "Free items and promotional gifts, except where the item itself is defective.",
        ],
      },
      {
        h: "6. How to raise a return, replacement or refund",
        list: [
          "Check the product and the seal at the door before accepting. The simplest resolution is to refuse a faulty item on the spot — the rider takes it back and we replace it.",
          `If you notice the problem later, report it within 24 hours of delivery. In the app, go to My Orders, select the order and use Report an issue — or email ${BRAND.email}, or call ${BRAND.phone}.`,
          "Include your order ID, what is wrong, and a photograph of the product, the seal and the batch or best-before marking. Photographs let us approve most claims the same day.",
          "Keep the item and its packaging until the claim is closed, so we can collect it if we need to.",
          "We acknowledge every claim within 24 hours and aim to decide within 48 hours of receiving the information we need.",
        ],
      },
      {
        h: "7. Replacement or refund — your choice",
        p: `Where a claim is approved you can have a free replacement on the next available delivery slot, or a refund. Collection of the faulty item, where we need it back, is free. We do not charge a restocking or return-shipping fee on an approved claim.`,
      },
      {
        h: "8. Refund method and timing",
        list: [
          "Digital wallet (eSewa, Khalti) — refunded to the same wallet, normally within 3 to 7 business days.",
          "Card — refunded to the same card, normally within 7 to 14 business days depending on your bank's settlement cycle.",
          "Bank transfer — refunded to the originating account within 7 to 14 business days. We may ask you to confirm the account details.",
          "Cash on delivery — refunded as Wateriia wallet credit, which is available immediately, or by bank transfer, whichever you prefer.",
          "Partial deliveries — you are charged only for what you accepted, and the balance is refunded on the same basis.",
        ],
        note: `The clock starts when we approve the claim, not when you raise it. Once we release a refund, the time it takes to appear in your account is controlled by your bank, wallet or card issuer. Keep the reference we give you — it is what the provider needs if you have to chase them.`,
      },
      {
        h: "9. Failed and duplicate payments",
        p: `If money left your account but the order did not go through, or you were charged twice, it is usually reversed automatically by the payment provider within 5 to 7 business days. If it has not been, email ${BRAND.email} with the date, the amount and the transaction reference, and we will trace it with the provider and refund you.`,
      },
      {
        h: "10. Returnable jar deposits",
        p: `Deposits paid on 20-litre returnable jars are refunded in full when the jars are returned undamaged and in usable condition. Hand them to any Wateriia rider, or ask us to arrange a pickup. Jars that are cracked, burnt, cut or contaminated, or that have been used for anything other than drinking water, cannot be accepted and the deposit for those units is forfeited. Request your deposit back by contacting ${BRAND.email} with your order or account details.`,
      },
      {
        h: "11. Deliveries we could not complete",
        p: `If nobody is available at your address, the rider will call your registered number and wait a reasonable time. If we still cannot hand the order over it returns to the store and you may reschedule. Repeated failed deliveries on prepaid orders are refunded, less any delivery charge actually incurred.`,
      },
      {
        h: "12. If you are not satisfied",
        p: `Escalate to us at ${BRAND.email} with your order ID and we will review the decision. We respond to escalations within 5 business days. Nothing in this policy limits the rights you have under the Consumer Protection Act, 2075 or other applicable Nepali law, including the right to approach the competent consumer authority.`,
      },
      {
        h: "13. Contact",
        p: `Returns, replacements and refunds: email ${BRAND.email} or call ${BRAND.phone}, quoting your order ID.`,
      },
    ],
  },

  "shipping-policy": {
    title: "Shipping & Delivery Policy",
    subtitle:
      "Where we deliver, how long it takes, what it costs, and what happens when something goes wrong.",
    sections: [
      {
        h: "1. Where we deliver",
        p: `We deliver across Kathmandu Valley — Kathmandu, Lalitpur and Bhaktapur — and selected surrounding municipalities. Coverage expands continuously. Enter your address or allow location access in the app to see whether your area is serviceable and what the charge will be before you pay.`,
      },
      {
        h: "2. Operating hours",
        p: `Deliveries run from 07:00 to 21:00 Nepal time, Sunday to Saturday. Orders placed outside those hours are queued for the next operating slot. Hours may be shortened on public holidays, during festivals, or in adverse weather; the app shows current availability.`,
      },
      {
        h: "3. Delivery speed",
        list: [
          "Standard — typically 45 to 120 minutes from order confirmation, within operating hours.",
          "Scheduled — pick a delivery window at checkout, subject to availability in your area.",
          "Subscription — delivered on the recurring schedule you set, with a reminder before each delivery.",
          "Bulk and commercial — a coordinator confirms the window with you directly.",
        ],
        note: `All times are estimates, not guarantees. They depend on distance, traffic, weather, rider availability and order volume.`,
      },
      {
        h: "4. Delivery charges",
        p: `Any delivery charge is calculated from your distance and order size and is shown at checkout before payment — nothing is added afterwards. Bulk orders, subscriptions and active promotions may qualify for free delivery, and the app shows what applies to your order.`,
      },
      {
        h: "5. Tracking your order",
        p: `Once a rider is assigned you can follow the order in the app — status updates from confirmed through dispatched to delivered, the rider's live position on a map, and the rider's contact number. We also send push notifications at each stage; enable notifications to receive them.`,
      },
      {
        h: "6. Receiving your delivery",
        list: [
          "Someone aged 18 or over should be available at the address during the delivery window.",
          "Check the seal, the quantity and the product before accepting. Refuse anything damaged or wrong — it is the fastest resolution.",
          "For cash on delivery, please have the exact amount ready.",
          "You may nominate another person to receive the order; delivery to them completes the order.",
          "Where our rider records a proof of delivery, it is kept as evidence that the order was handed over.",
        ],
      },
      {
        h: "7. Access and multi-storey buildings",
        p: `Riders deliver to your door where it is safe and practical to do so. For buildings without a working lift, very long internal distances, or restricted-access premises, please tell us in the delivery instruction so we can plan. Where access is genuinely unsafe, the rider will hand over at the nearest safe point.`,
      },
      {
        h: "8. Failed deliveries",
        p: `If nobody is available, the rider calls your registered number and waits a reasonable time — normally about 15 minutes. If we still cannot complete the delivery, the order returns to the store and you can reschedule from the app. A re-delivery charge may apply where a delivery fails for reasons within your control. Prepaid orders that cannot be delivered are refunded under the Return & Refund Policy.`,
      },
      {
        h: "9. Delays outside our control",
        p: `Traffic, road works, weather, landslides, fuel shortages, bandhs, strikes, festivals, civil unrest and government restrictions can delay a delivery. We will keep you informed in the app and deliver as soon as we safely can. Where a delay makes the delivery pointless for you, you may cancel and be refunded in full.`,
      },
      {
        h: "10. Returnable jars",
        p: `Please have empty jars ready when the rider arrives, so they can be exchanged in a single visit. Jars remain our property — see the Terms & Conditions for how to look after them and how deposits work.`,
      },
      {
        h: "11. Contact",
        p: `Delivery questions: call ${BRAND.phone} or email ${BRAND.email} with your order ID.`,
      },
    ],
  },

  "data-retention-policy": {
    title: "Data Retention Policy",
    subtitle:
      "Exactly how long Wateriia keeps each category of data, why, and what happens at the end of the period.",
    storeNote:
      "Published to support the data-handling disclosures in our Google Play and App Store listings.",
    sections: [
      {
        h: "1. Purpose and principle",
        p: `We keep personal data only for as long as it serves the purpose it was collected for, or as long as Nepali law requires us to keep it. When a retention period ends, the data is deleted or irreversibly anonymised so that it can no longer identify you. This policy sets out the periods we apply, and covers ${BRAND.legal} and the processors that act on our instructions.`,
      },
      {
        h: "2. Retention schedule",
        p: `Each period below runs from the trigger stated in its line. Where two periods apply to the same record, the longer one governs.`,
        list: [
          "Account profile — name, phone, email, password hash, language — kept while your account is active, and deleted within 30 days of a verified deletion request or after 36 months of complete inactivity, whichever comes first.",
          "Delivery addresses and saved delivery instructions — kept while your account is active, and deleted with the account.",
          "Order and invoice records — items, amounts, taxes, the delivery address printed on the invoice — kept 5 years from the end of the financial year of the transaction, to meet Nepali tax and accounting requirements. These survive account deletion because the law requires it.",
          "Payment records — method, amount, provider reference, status, refunds — kept 5 years from the transaction, for reconciliation, audit, chargeback and anti-fraud purposes. We never hold full card numbers, CVVs or wallet PINs at any point.",
          "Returnable-jar deposit ledger — kept until the deposit is refunded or forfeited, then 5 years as a financial record.",
          "Subscription schedules and delivery history — kept while the subscription is active, then 24 months, then deleted or anonymised.",
          "Support tickets, disputes and attachments — kept 24 months from closure, so that we have the history if you contact us again. A complaint record connected to a legal claim is kept until that claim is finally resolved.",
          "Contact-form, enquiry and partner-application submissions — kept 24 months from the last contact, unless a business relationship starts, in which case they become part of that record.",
          "OTPs and verification codes — single-use, and the code itself is invalidated within minutes. The verification event — number, time, outcome — is kept 12 months for security and anti-fraud purposes.",
          "Login sessions and device tokens — revoked on sign-out or account deletion; expired session records are purged within 90 days.",
          "Push notification tokens — kept while the app is installed and notifications are enabled, and removed when you uninstall, disable notifications, or delete your account.",
          "Precise location captured for an active delivery — retained for that delivery and for up to 30 days afterwards for dispute resolution, then deleted. We do not keep a continuous history of your movements.",
          "Proof of delivery — rider confirmation, and a photograph where one is taken — kept 12 months from delivery, as evidence that the order was handed over.",
          "Server, access and error logs including IP addresses — kept 90 days for security monitoring and fault diagnosis, then deleted. A log preserved for an active security investigation is kept until that investigation closes.",
          "Audit logs of administrative actions on your data — kept 5 years, because their whole purpose is accountability. They record who did what and when, and are not deleted on request.",
          "Analytics and product-usage data — pseudonymised at collection and retained up to 26 months, after which it is aggregated so that no individual can be identified.",
          "Marketing consent and opt-out records — the record that you opted out is kept indefinitely, because that is how we make sure we do not contact you again.",
          "Backups — retained on a rolling 35-day cycle. Deleted records disappear from backups as that cycle turns; we do not restore a backup to recover data you asked us to delete.",
        ],
      },
      {
        h: "3. Why some data outlives your account",
        p: `Deleting your account removes your profile and the data we hold in order to serve you. It cannot remove records the law obliges us to keep — invoices, tax and payment records, and audit trails. Those are retained for the periods above, are restricted to the staff who need them for compliance, and are never used to market to you or to rebuild your account. Data we must keep for a legal claim, an investigation, or a lawful request from an authority is also held until that matter closes.`,
      },
      {
        h: "4. What deletion means in practice",
        list: [
          "Records are deleted from live systems, or irreversibly anonymised where a statistic has to be preserved.",
          "Deletion is propagated to our processors, who are contractually required to delete on our instruction.",
          "Backups age out on their normal cycle rather than being edited, which is why deletion completes within 35 days of the live deletion rather than instantly.",
          "Physical documents, where any exist, are shredded.",
        ],
      },
      {
        h: "5. Your control",
        p: `You can edit or remove your addresses and profile details in the app at any time, turn off location and notification permissions in your device settings, unsubscribe from marketing with one tap, and request full deletion of your account — see Delete Your Account & Data. To ask what we hold about you, or for a copy of it, email ${BRAND.email} from your registered address.`,
      },
      {
        h: "6. Review and changes",
        p: `We review this policy at least once a year, and whenever the law or our systems change materially. The version in force is the one published here. Material changes are notified in the app or by email before they take effect.`,
      },
      {
        h: "7. Contact",
        p: `Questions about retention, or about a specific record: email ${BRAND.email}, or write to ${BRAND.legal}, ${BRAND.address}.`,
      },
    ],
  },

  "account-deletion": {
    title: "Data & Account Deletion Policy",
    subtitle:
      "How to permanently delete your Wateriia account and the personal data attached to it — what goes, what we must keep, and how long it takes.",
    storeNote:
      "This is the account and data deletion page referenced by the Wateriia listing on Google Play, as required by Google Play's data deletion policy.",
    sections: [
      {
        h: "1. Before you start",
        list: [
          "Deletion is permanent. Your order history, saved addresses, subscriptions and wallet credit cannot be restored afterwards.",
          "Cancel or let any active subscription finish first, so that you are not expecting a delivery that will not arrive.",
          "Settle any unpaid balance, and return any Wateriia returnable jars you hold, so that the deposit can be refunded to you before the account closes.",
          "Redeem or withdraw any wallet credit. Unredeemed promotional credit has no cash value and lapses on deletion.",
          "If you only want the marketing messages to stop, you do not need to delete your account — unsubscribe from any marketing message, or turn off notifications in your device settings.",
        ],
      },
      {
        h: "2. Option A — delete from the Wateriia app",
        p: `Open the Wateriia app, go to Profile or Account, choose Delete account, and confirm. We verify the request with an OTP to your registered mobile number and then queue the deletion, and you will receive a confirmation once it completes. If your version of the app does not yet show this option, update the app or use Option B.`,
      },
      {
        h: "3. Option B — request deletion by email or phone",
        p: `Send an email to ${BRAND.email} from the email address on your account, with the subject "Delete my account", stating the mobile number registered with Wateriia. Or call ${BRAND.phone} during business hours and ask for account deletion. You can also write to us at ${BRAND.legal}, ${BRAND.address}.`,
        list: [
          "We verify it is really you — normally by an OTP to your registered mobile number — before we delete anything. This protects you from someone else closing your account.",
          "We acknowledge your request within 48 hours.",
          "We complete the deletion within 30 days of verifying your identity, and confirm when it is done.",
          "There is no charge for deleting your account or your data.",
        ],
      },
      {
        h: "4. What is deleted",
        list: [
          "Your account and profile — name, email address, mobile number, password hash and language preference.",
          "All saved delivery addresses and delivery instructions.",
          "Your active carts and any saved items.",
          "Subscription schedules and future scheduled deliveries.",
          "Push notification tokens, and login sessions on every device.",
          "Marketing preferences and audience memberships — you are also removed from future campaigns.",
          "Location data held for past deliveries, and proof-of-delivery images linked to you, once their short retention period has run.",
          "Support tickets and their attachments, once their retention period has run, unless a ticket is part of an unresolved dispute or legal claim.",
        ],
      },
      {
        h: "5. What we have to keep, and for how long",
        p: `Some records cannot be deleted on request, because Nepali law requires us to keep them. These are retained in restricted-access systems, are not used to contact you or to market to you, and are not used to rebuild your account.`,
        list: [
          "Invoices and order records — 5 years from the end of the financial year of the transaction, under Nepali tax and accounting law.",
          "Payment and refund records — 5 years, for reconciliation, audit and chargeback handling. These hold the transaction reference and amount, never your card or wallet credentials.",
          "Returnable-jar deposit ledger entries — 5 years as a financial record, after the deposit is refunded or forfeited.",
          "Audit logs of administrative actions — 5 years, because their purpose is accountability.",
          "Anything subject to an active legal claim, dispute, investigation or lawful request from an authority — until that matter is finally resolved.",
          "The record that you asked to be deleted and to be excluded from marketing — kept so that we can honour it.",
        ],
        note: `Where a record must be kept, we minimise it — removing the fields that are not legally required, and retaining only what the obligation covers.`,
      },
      {
        h: "6. Backups",
        p: `Deleted data is removed from our live systems immediately on completion, and disappears from our encrypted backups as they age out on a rolling 35-day cycle. We do not restore a backup to recover data you asked us to delete.`,
      },
      {
        h: "7. Third parties",
        p: `We instruct our processors — hosting, database, push notification, SMS, email and analytics providers — to delete your data when we delete it, as their contracts require. Payment providers keep their own transaction records under their own legal obligations and privacy policies; we cannot delete those on your behalf, and you would need to contact the provider directly.`,
      },
      {
        h: "8. Deleting the app is not deleting your account",
        p: `Uninstalling the Wateriia app removes it from your device and stops push notifications, but your account and data stay with us until you ask us to delete them. Use Option A or Option B above.`,
      },
      {
        h: "9. Coming back later",
        p: `You are welcome to sign up again at any time with the same mobile number, but it will be a new account — the previous order history, addresses, subscriptions and credit cannot be recovered.`,
      },
      {
        h: "10. If something goes wrong",
        p: `If you do not hear from us within 48 hours of a deletion request, or the deletion is not completed within 30 days, escalate to ${BRAND.email} marking your email "Escalation — account deletion", or call ${BRAND.phone}. You may also approach the competent authority in Nepal if you remain dissatisfied.`,
      },
      {
        h: "11. Contact",
        p: `Deletion requests and questions: ${BRAND.email} · ${BRAND.phone} · ${BRAND.legal}, ${BRAND.address}.`,
      },
    ],
  },

  "cookie-policy": {
    title: "Cookie Policy",
    subtitle: "The cookies and local storage the Wateriia website and app use, and how to control them.",
    sections: [
      {
        h: "1. What we mean by cookies",
        p: `Cookies are small text files a website stores on your device. We also use similar technologies — browser local storage, session storage, and secure on-device storage in the mobile app — for the same purposes. This policy covers all of them.`,
      },
      {
        h: "2. Cookies we use",
        list: [
          "Strictly necessary — keep you signed in, remember what is in your cart, protect forms against cross-site request forgery, balance load across our servers, and enforce rate limits. The site cannot work without these, so they are not optional.",
          "Preference — remember your language, your last delivery area and display settings, so you do not have to set them again on every visit.",
          "Analytics and performance — measure which pages and features are used, how fast they load, and where errors occur, so that we can fix and improve things. These are pseudonymised and set only where permitted.",
          "Session replay and diagnostics — capture anonymised interaction traces that help us reproduce a fault you report.",
        ],
        note: `We do not use cookies for third-party advertising, and we do not sell data collected through cookies.`,
      },
      {
        h: "3. Third-party cookies",
        p: `Some features are provided by third parties that may set their own cookies — payment gateways during checkout, embedded maps, and our analytics provider. Their use of cookies is governed by their own policies. Blocking payment or map cookies may stop checkout or the map from working.`,
      },
      {
        h: "4. How long they last",
        p: `Session cookies are deleted when you close the browser. Persistent cookies last from a few days up to 24 months depending on their purpose, and are refreshed while you keep using the site. In the app, on-device storage persists until you sign out, clear the app's data, or uninstall it.`,
      },
      {
        h: "5. Controlling cookies",
        list: [
          "Browser — every major browser lets you view, block and delete cookies in its settings, and offers a private browsing mode. Blocking strictly necessary cookies will break sign-in and checkout.",
          "App — sign out to clear your session, or clear the app's storage from your device settings.",
          "Analytics — you can opt out of analytics cookies from your browser settings, or by using a browser that blocks them. The service still works.",
          "Do Not Track — we do not serve cross-site advertising, so there is no ad tracking to disable.",
        ],
      },
      {
        h: "6. Changes",
        p: `If we add or change the cookies we use, we will update this page and its "last updated" date.`,
      },
      {
        h: "7. Contact",
        p: `Questions about cookies: ${BRAND.email}.`,
      },
    ],
  },

  "child-safety-policy": {
    title: "Child Safety Policy",
    subtitle:
      "Wateriia's standards against child sexual abuse and exploitation, and how to report a concern.",
    storeNote:
      "Published to meet Google Play's Child Safety Standards requirements and Apple's App Store safety expectations.",
    sections: [
      {
        h: "1. Our position",
        p: `Wateriia has zero tolerance for child sexual abuse and exploitation (CSAE), for child sexual abuse material (CSAM), and for any content or behaviour that sexualises, endangers or exploits a minor. This is an absolute rule with no exception, and it applies equally to customers, staff, riders and partner stores.`,
      },
      {
        h: "2. Wateriia is an adult-only service",
        list: [
          "You must be 18 or over to create an account and place an order.",
          "The app is not designed for, marketed to, or intended to appeal to children.",
          "The app has no social network, no public profiles, no messaging between users, no comments and no content feed. Users cannot contact one another through Wateriia.",
          "The only content a user can submit is an order note or a support ticket, including an attached image, sent privately to our support team. Nothing a user submits is published to anyone else.",
        ],
      },
      {
        h: "3. What is prohibited",
        p: `It is forbidden to use any part of Wateriia — including order notes, support tickets, attachments, contact forms and reviews — to:`,
        list: [
          "Share, request, describe, solicit or link to child sexual abuse material of any kind.",
          "Sexualise a minor, or depict a minor in a sexual, suggestive or exploitative manner.",
          "Groom, solicit, threaten, coerce, or attempt to arrange contact with a minor.",
          "Traffic, advertise or facilitate the exploitation of a minor.",
          "Impersonate a minor, or misrepresent your age to gain access.",
        ],
      },
      {
        h: "4. How we enforce this",
        list: [
          "Support tickets and attachments are reviewed by trained staff before any action is taken on them.",
          "Any account involved in prohibited conduct is suspended immediately and permanently closed.",
          "We preserve the relevant evidence and report the matter to the Nepal Police Cyber Bureau and any other competent authority, and cooperate fully with their investigation.",
          "Where a platform such as Google or Apple requires notification, we notify them.",
          "Staff who handle user content are trained to recognise and escalate CSAE concerns, and escalation is mandatory, not discretionary.",
        ],
      },
      {
        h: "5. Reporting a concern",
        p: `If you see anything on or through Wateriia that endangers or exploits a child, report it immediately:`,
        list: [
          `Email ${BRAND.email} with the subject "CHILD SAFETY — URGENT". Reports with this subject are escalated on receipt.`,
          `Call ${BRAND.phone} and ask to speak to the child safety point of contact.`,
          "In an emergency, or where a child is at immediate risk, contact the Nepal Police on 100 or the Nepal Police Cyber Bureau directly — do not wait for us.",
        ],
        note: `Please do not forward, download or re-share suspected abuse material, even in order to report it — describe what you saw and where, and we will retrieve it properly. Reports are handled confidentially, and we will not retaliate against anyone who reports in good faith.`,
      },
      {
        h: "6. Point of contact",
        p: `Child safety point of contact: ${BRAND.legal}, ${BRAND.address} — ${BRAND.email}, ${BRAND.phone}. We aim to acknowledge a child safety report within 24 hours and to act on it immediately.`,
      },
      {
        h: "7. Review",
        p: `These standards are reviewed at least annually, and updated whenever the law or platform requirements change.`,
      },
    ],
  },

  "ecommerce-disclaimer": {
    title: "E-commerce Disclaimer",
    subtitle: "The limits of what our platform and its content represent.",
    sections: [
      {
        h: "1. What the platform does",
        p: `Wateriia Nepal, operated by ${BRAND.legal}, provides a website and a mobile application through which you can order bottled drinking water and related products for delivery to serviceable addresses in Nepal. We sell and deliver the listed products ourselves or through our partner stores.`,
      },
      {
        h: "2. Product information",
        p: `We take reasonable care to keep descriptions, images, volumes and prices accurate at the time of publication. Packaging, label design and bottle appearance may vary between batches, and images are illustrative. Where information differs, what is shown at the moment you confirm your order is what applies.`,
      },
      {
        h: "3. Pricing and listing errors",
        p: `Despite our checks, an occasional pricing or listing error is possible. Where a price is obviously wrong, we may decline or cancel the affected order and refund you in full rather than supply at that price. We will tell you as soon as we notice.`,
      },
      {
        h: "4. Availability and serviceability",
        p: `Listing a product or an area does not guarantee it is available at the moment you order. Stock, delivery slots and coverage change continuously. Where we cannot fulfil an order, we will tell you and refund you in full.`,
      },
      {
        h: "5. No medical or nutritional claims",
        p: `Wateriia drinking water is for general hydration and ordinary household or commercial use. We make no medical, therapeutic or nutritional claim about it. Nothing in the app or on the website is health advice — consult a qualified professional for that.`,
      },
      {
        h: "6. Third-party content and links",
        p: `The platform includes third-party services and links — payment gateways, maps, social media, partner websites. We do not control them, we are not responsible for their content or their practices, and a link is not an endorsement.`,
      },
      {
        h: "7. Reviews and testimonials",
        p: `Testimonials reflect the experience of the individual who gave them, and are published with permission. They are not a promise of the outcome you will get.`,
      },
      {
        h: "8. Technical accuracy",
        p: `Delivery estimates, distances, map positions and slot availability are calculated automatically and are approximations. We do not warrant that the platform will be free of error or interruption.`,
      },
      {
        h: "9. Limitation",
        p: `To the maximum extent permitted by Nepali law, Wateriia is not liable for indirect, incidental, special or consequential loss arising from use of the platform. This disclaimer does not affect your statutory consumer rights, or the commitments we make in the Terms & Conditions and the Return & Refund Policy.`,
      },
      {
        h: "10. Contact",
        p: `Questions about anything on this page: ${BRAND.email} · ${BRAND.phone}.`,
      },
    ],
  },

  support: {
    title: "Support & Grievances",
    subtitle: "How to reach a person at Wateriia, what to expect, and how to escalate.",
    storeNote:
      "This is the support page referenced by the Wateriia listing on the Apple App Store and Google Play.",
    sections: [
      {
        h: "1. How to reach us",
        list: [
          "In the app — open Support or Help, or select an order and choose Report an issue. This is the fastest route, because your order details travel with the ticket.",
          `Email — ${BRAND.email}`,
          `Phone — ${BRAND.phone}, 07:00 to 21:00 Nepal time, Sunday to Saturday.`,
          `WhatsApp — message ${BRAND.phone} for quick order questions.`,
          `Post — ${BRAND.legal}, ${BRAND.address}`,
        ],
      },
      {
        h: "2. What to include",
        p: `Give us your registered mobile number, the order ID if your question is about an order, a short description of the problem, and a photograph where something arrived damaged or wrong. That is usually enough for us to resolve it on first contact.`,
      },
      {
        h: "3. Response times we aim for",
        list: [
          "Delivery in progress — call us; we respond immediately during operating hours.",
          "Any ticket or email — acknowledged within 24 hours.",
          "Damaged, wrong or short delivery — decision within 48 hours of receiving your photographs.",
          "Refunds — released within 48 hours of approval, then 3 to 14 business days to reach you depending on the payment method.",
          "Privacy and data requests — acknowledged within 48 hours, completed within 30 days.",
          "Everything else — within 5 business days.",
        ],
      },
      {
        h: "4. Escalation",
        p: `If your issue is not resolved, or you are unhappy with the outcome, reply to your ticket asking for escalation, or email ${BRAND.email} with "Escalation" in the subject and your ticket or order ID. A senior member of the team reviews escalations and responds within 5 business days.`,
      },
      {
        h: "5. Your statutory rights",
        p: `Nothing in our policies limits the rights you have under the Consumer Protection Act, 2075 or other applicable Nepali law. If we cannot resolve your complaint, you may approach the competent consumer protection authority in Nepal.`,
      },
      {
        h: "6. Reporting security and safety issues",
        list: [
          `Security vulnerability in our app, website or API — email ${BRAND.email} with "SECURITY" in the subject. Please give us reasonable time to fix it before disclosing it publicly; we will not pursue good-faith researchers who follow this.`,
          `Privacy concern or suspected data breach — email ${BRAND.email} with "PRIVACY" in the subject.`,
          "Child safety concern — see our Child Safety Policy for the urgent reporting route.",
          `Unsafe or abusive conduct by a rider or a member of staff — call ${BRAND.phone} and ask for a supervisor. We investigate every report.`,
        ],
      },
      {
        h: "7. Our policies",
        p: `The Privacy Policy, Terms & Conditions, Return & Refund Policy, Shipping & Delivery Policy, Data Retention Policy, Delete Your Account & Data, Cookie Policy, Child Safety Policy and E-commerce Disclaimer are all linked from the footer of every page, and from the sidebar of this one.`,
      },
    ],
  },
};
